import { RoomManagement } from '../RoomManagement';
import { useState } from 'react';
import { OverlayPortal } from '../OverlayPortal';
import { MaterialIcon } from '../MaterialIcon';
import { useGame } from '../../state/gameContext';
import { useSound } from '../../state/soundManager';

type Panel = 'chat' | 'activity' | 'settings' | null;

interface GameMenuProps {
  /** Show the chat button (only meaningful inside an active room). */
  showChat?: boolean;
  /** Show the in-game leave action inside the settings modal. */
  showLeave?: boolean;
  /** Compact buttons for dense in-game headers. */
  compact?: boolean;
}

/**
 * Self-contained top-right game controls: notifications (activity / win-log),
 * chat, and settings. Each button deterministically opens exactly one overlay;
 * overlays are position:fixed so this can be embedded in any header.
 */
export function GameMenu({ showChat = false, showLeave = false, compact = false }: GameMenuProps) {
  const [panel, setPanel] = useState<Panel>(null);
  const close = () => setPanel(null);
  const toggle = (next: Exclude<Panel, null>) =>
    setPanel((prev) => (prev === next ? null : next));

  const btn = [
    'text-on-surface-variant hover:text-secondary transition-colors flex items-center justify-center rounded-full',
    compact ? 'p-1.5' : 'p-2',
  ].join(' ');

  return (
    <>
      <div className="flex items-center gap-1 md:gap-2">
        {showChat && (
          <button
            type="button"
            className={btn}
            aria-label="Chat"
            data-testid="chat-button"
            aria-expanded={panel === 'chat'}
            onClick={() => toggle('chat')}
          >
            <MaterialIcon name="forum" />
          </button>
        )}
        <button
          type="button"
          className={btn}
          aria-label="Notifications"
          data-testid="notifications-button"
          aria-expanded={panel === 'activity'}
          onClick={() => toggle('activity')}
        >
          <MaterialIcon name="notifications" />
        </button>
        <button
          type="button"
          className={btn}
          aria-label="Settings"
          data-testid="settings-button"
          aria-expanded={panel === 'settings'}
          onClick={() => toggle('settings')}
        >
          <MaterialIcon name="settings" />
        </button>
      </div>

      {panel === 'chat' && <ChatDrawer onClose={close} />}
      {panel === 'activity' && <ActivityDrawer onClose={close} />}
      {panel === 'settings' && <SettingsModal onClose={close} showLeave={showLeave} />}
    </>
  );
}

export function DrawerShell({
  title,
  icon,
  testId,
  onClose,
  children,
}: {
  title: string;
  icon: string;
  testId: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <OverlayPortal onClose={onClose}>
      <div className="fixed inset-0 z-[90] bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed right-0 top-0 h-full z-[95] w-[88vw] max-w-sm flex flex-col p-stack-md bg-surface-container border-l border-white/10 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-stack-md border-b border-white/10 pb-4">
          <div className="flex items-center gap-2">
            <MaterialIcon name={icon} className="text-secondary" />
            <h2 className="font-headline-md text-[20px] text-on-surface">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="panel-close"
            className="text-on-surface-variant hover:text-on-surface p-1"
          >
            <MaterialIcon name="close" />
          </button>
        </div>
        {children}
      </aside>
    </OverlayPortal>
  );
}

export function ChatDrawer({ onClose }: { onClose: () => void }) {
  const { state, runtimeMode } = useGame();
  const [draft, setDraft] = useState('');
  const messages = state.log.slice(-50);

  return (
    <DrawerShell title="Chat" icon="forum" testId="chat-panel" onClose={onClose}>
      <div className="flex-grow overflow-y-auto custom-scrollbar flex flex-col gap-3 text-sm">
        {messages.length === 0 && (
          <p className="text-on-surface-variant italic text-center mt-8">No messages yet.</p>
        )}
        {messages.map((msg) =>
          msg.system ? (
            <p key={msg.id} className="text-on-surface-variant/70 italic text-center text-xs">
              {msg.text}
            </p>
          ) : (
            <div key={msg.id} className={`flex flex-col gap-0.5 ${msg.self ? 'items-end' : ''}`}>
              <span className="text-[10px] uppercase font-bold tracking-tight text-primary/60">
                {msg.author}
              </span>
              <div
                className={`p-2.5 rounded-lg border max-w-[90%] ${
                  msg.self
                    ? 'bg-secondary-container/20 border-secondary/20'
                    : 'bg-surface-container-high border-white/5'
                }`}
              >
                <p className="text-on-surface">{msg.text}</p>
              </div>
            </div>
          ),
        )}
      </div>
      <form
        className="mt-4 pt-4 border-t border-white/10"
        onSubmit={(e) => {
          e.preventDefault();
          setDraft('');
        }}
      >
        <input
          data-testid="chat-input"
          value={draft}
          maxLength={240}
          onChange={(e) => setDraft(e.target.value)}
          disabled={runtimeMode === 'server'}
          className="w-full bg-black/30 border border-white/10 rounded-lg py-2 px-3 text-on-surface focus:outline-none focus:border-primary disabled:opacity-50"
          placeholder={
            runtimeMode === 'server' ? 'Live room chat coming soon' : 'Type a message…'
          }
        />
        {runtimeMode === 'server' && (
          <p className="text-[10px] text-on-surface-variant/60 mt-1">
            Room chat is a placeholder in live mode for now.
          </p>
        )}
      </form>
    </DrawerShell>
  );
}

function ActivityDrawer({ onClose }: { onClose: () => void }) {
  const { state, runtimeMode, connectionStatus } = useGame();
  const history = [...state.roundHistory].reverse();

  const phaseLabel: Record<string, string> = {
    none: 'Not started',
    lobby: 'In lobby',
    playing: 'Players choosing cards',
    judging: 'Judge is deciding',
    roundResult: 'Round complete',
    gameOver: 'Game over',
  };

  return (
    <DrawerShell title="Activity" icon="notifications" testId="notifications-panel" onClose={onClose}>
      <div className="flex-grow overflow-y-auto custom-scrollbar flex flex-col gap-stack-md text-sm">
        <section>
          <h3 className="text-label-sm uppercase tracking-wide text-on-surface-variant/70 mb-2">
            Now
          </h3>
          <ul className="flex flex-col gap-1.5 text-on-surface-variant">
            {state.roundNumber > 0 && (
              <li>
                Round {state.roundNumber} — {phaseLabel[state.phase] ?? state.phase}
              </li>
            )}
            {state.phase === 'playing' && state.submittedCount !== undefined && (
              <li data-testid="activity-submitted">{state.submittedCount} card(s) submitted</li>
            )}
            {runtimeMode === 'server' && <li>Connection: {connectionStatus}</li>}
          </ul>
        </section>

        <section data-testid="win-log">
          <h3 className="text-label-sm uppercase tracking-wide text-on-surface-variant/70 mb-2">
            Winning cards
          </h3>
          {history.length === 0 ? (
            <p className="text-on-surface-variant italic">No rounds won yet.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {history.map((entry) => (
                <li
                  key={entry.roundNumber}
                  data-testid="win-log-entry"
                  className="rounded-lg border border-white/10 bg-surface-container-high p-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] uppercase font-bold text-on-surface-variant/60">
                      Round {entry.roundNumber}
                    </span>
                    <span className="text-secondary font-label-bold text-label-bold">
                      {entry.winnerName}
                    </span>
                  </div>
                  <p className="text-on-surface-variant text-xs mb-1">{entry.promptText}</p>
                  <p className="text-on-surface font-card-text">{entry.answerText}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </DrawerShell>
  );
}

function SettingsModal({ onClose, showLeave }: { onClose: () => void; showLeave: boolean }) {
  const { state, reset } = useGame();
  const { volume, muted, setVolume, setMuted } = useSound();
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [copied, setCopied] = useState(false);
  const inProgress = ['playing', 'judging', 'roundResult'].includes(state.phase);

  const copyCode = async () => {
    if (!state.roomCode) return;
    try {
      await navigator.clipboard?.writeText(state.roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <OverlayPortal onClose={onClose}>
      <div className="fixed inset-0 z-[90] bg-black/60" onClick={onClose} aria-hidden="true" />
      <div
        data-testid="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[95] w-[92vw] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain flex flex-col gap-stack-md p-stack-lg rounded-2xl bg-surface-container border border-white/10 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MaterialIcon name="settings" className="text-secondary" />
            <h2 className="font-headline-md text-[22px] text-on-surface">Settings</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="settings-close"
            className="text-on-surface-variant hover:text-on-surface p-1"
          >
            <MaterialIcon name="close" />
          </button>
        </div>

        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <label htmlFor="volume-slider" className="font-label-bold text-label-bold">
              Volume
            </label>
            <button
              type="button"
              data-testid="mute-toggle"
              aria-pressed={muted}
              onClick={() => setMuted(!muted)}
              className="flex items-center gap-1 text-on-surface-variant hover:text-on-surface"
            >
              <MaterialIcon name={muted ? 'volume_off' : 'volume_up'} />
              <span className="text-label-sm">{muted ? 'Muted' : 'On'}</span>
            </button>
          </div>
          <input
            id="volume-slider"
            data-testid="volume-slider"
            type="range"
            min={0}
            max={100}
            value={Math.round(volume * 100)}
            onChange={(e) => setVolume(Number(e.target.value) / 100)}
            className="custom-slider w-full accent-primary"
          />
        </section>

        {state.roomCode && (
          <section className="flex items-center justify-between rounded-lg border border-white/10 bg-surface-container-high px-4 py-3">
            <div>
              <p className="text-[10px] uppercase text-on-surface-variant/60">Room code</p>
              <p className="font-label-bold text-label-bold text-secondary">{state.roomCode}</p>
            </div>
            <button
              type="button"
              data-testid="copy-room-code"
              onClick={copyCode}
              className="flex items-center gap-1 text-on-surface-variant hover:text-secondary"
            >
              <MaterialIcon name={copied ? 'check' : 'content_copy'} />
              <span className="text-label-sm">{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </section>
        )}

        {state.roomCode && (
          <section className="rounded-lg border border-white/10 bg-surface-container-high px-4 py-3 text-sm text-on-surface-variant">
            <p className="text-[10px] uppercase text-on-surface-variant/60 mb-1">Game settings</p>
            <p>Points to win: {state.settings.pointsToWin}</p>
            <p>Max players: {state.settings.maxPlayers}</p>
            <p>Wild cards: {state.settings.wildCardsEnabled ? 'On' : 'Off'}</p>
          </section>
        )}

        {state.roomCode && <RoomManagement />}

        {showLeave && state.roomCode && (
          <section className="mt-1">
            {!confirmLeave ? (
              <button
                type="button"
                data-testid="leave-game"
                onClick={() => (inProgress ? setConfirmLeave(true) : reset())}
                className="w-full py-3 rounded-lg bg-error/15 border border-error/40 text-error font-label-bold hover:bg-error/25 transition-colors flex items-center justify-center gap-2"
              >
                <MaterialIcon name="logout" />
                Leave Game
              </button>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-center text-label-sm text-on-surface-variant">
                  Leave the game in progress? You can rejoin with the room code.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmLeave(false)}
                    className="flex-1 py-2.5 rounded-lg border border-white/10 text-on-surface-variant hover:bg-surface-variant/40"
                  >
                    Stay
                  </button>
                  <button
                    type="button"
                    data-testid="confirm-leave-game"
                    onClick={() => reset()}
                    className="flex-1 py-2.5 rounded-lg bg-error text-on-error font-label-bold hover:brightness-110"
                  >
                    Leave
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </OverlayPortal>
  );
}
